import {useAuthStore} from "@/stores/authStore"
import {useRouter} from 'vue-router'

// Screens a user may pick to open on login, with the menu key that must allow it
export const LANDING_PAGES = [
    {value: 'dashboard', title: 'Dashboard', key: 'dashboard'},
    {value: 'live-view', title: 'Live Map', key: 'live_view'},
    {value: 'activity', title: 'Activity', key: 'notifications'},
];

export function landingPagesFor(menus) {
    const keys = menus ? Object.keys(menus) : null;
    return LANDING_PAGES.filter(page => !keys || keys.includes(page.key));
}

// The saved landing screen when the role can open it, else Dashboard, else Profile
export function landingRoute(auth) {
    const allowed = landingPagesFor(auth.menus).map(page => page.value);
    const choice = auth.configs?.landing_page;
    if (choice && allowed.includes(choice)) return choice;
    return allowed.includes('dashboard') ? 'dashboard' : 'profile';
}

// Define the navigateTo composable
export function useNavigation() {
    const router = useRouter()

    // Function to navigate to a specific route
    function navigateTo(routeName, params = {}) {
        if (params && Object.keys(params).length) {
            router.push({name: routeName, params});
        } else if (router?.push) {
            router.push({name: routeName});
        }
    }

    return {navigateTo}
}

export function strOperations() {
    // Function to Capitalize string
    function capitalize(string) {
        if (string) {
            return string.split(' ').map((str) => {
                return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
            }).join(' ')
        }
    }

    function formatEnumString(string) {
        return string
            .toLowerCase() // Convert to lowercase: "motion_in_progress"
            .replace(/_/g, ' ')   // Replace underscores with spaces: "motion in progress"
            .replace(/\b\w/g, c => c.toUpperCase()); // Capitalize first letter of each word: "Motion In Progress"
    }

    function formatVoltage(voltage) {
        voltage = parseFloat(voltage);
        return (isNaN(voltage)) ? '0v' : `${(voltage / 1000).toFixed(2)}v`;
    }

    function unitifyDistance(speed, type = 'speed', baseUnit = 'miles') {
        const authStore = useAuthStore();
        const userPrefersKm = authStore.configs?.distance_unit === 'km';

        if (!speed) return speed;

        const parsedSpeed = parseFloat(speed);
        let convertedValue = parsedSpeed;
        let unitLabel = '';

        // Conversion logic
        if (userPrefersKm && baseUnit === 'miles') {
            convertedValue = parsedSpeed * 1.609;
        } else if (!userPrefersKm && baseUnit === 'km') {
            convertedValue = parsedSpeed / 1.609;
        }

        // Unit label logic
        if (type === 'speed') {
            unitLabel = userPrefersKm ? 'kph' : 'mph';
        } else if (type === 'distance') {
            unitLabel = userPrefersKm ? 'km' : 'mi';
        } else {
            unitLabel = type; // fallback for custom types
        }

        return `${convertedValue.toFixed(2)}${unitLabel}`;
    }

    // Engine readings arrive in metric; miles users get gal, F and psi.
    function prefersMetric() {
        return useAuthStore().configs?.distance_unit === 'km';
    }

    function unitLabels() {
        const metric = prefersMetric();
        return {volume: metric ? 'L' : 'gal', temperature: metric ? '°C' : '°F', pressure: metric ? 'kPa' : 'psi'};
    }

    // Pass withUnit = false to get the converted number, for charts.
    function convertMeasure(value, toImperial, digits, unit, withUnit) {
        if (value === null || value === undefined || value === '') return null;
        const parsed = parseFloat(value);
        if (isNaN(parsed)) return null;
        const converted = prefersMetric() ? parsed : toImperial(parsed);
        const rounded = Number(converted.toFixed(digits));
        return withUnit ? `${rounded.toLocaleString(undefined, {maximumFractionDigits: digits})} ${unit}` : rounded;
    }

    function unitifyVolume(liters, withUnit = true) {
        return convertMeasure(liters, l => l / 3.78541, 1, unitLabels().volume, withUnit);
    }

    function unitifyTemperature(celsius, withUnit = true) {
        return convertMeasure(celsius, c => c * 9 / 5 + 32, 0, unitLabels().temperature, withUnit);
    }

    function unitifyPressure(kpa, withUnit = true) {
        return convertMeasure(kpa, k => k * 0.145038, prefersMetric() ? 0 : 1, unitLabels().pressure, withUnit);
    }

    /**
     * Resolve a speed in MPH from an object that may or may not carry the
     * server-converted value.
     *
     * The raw gps_speed column carries two scales: Orion hardware writes MPH,
     * the phone app writes km/h x2. Only the server knows which device wrote a
     * row, so it now sends a pre-converted speed_mph alongside the raw field
     * (REST resources and, for the live map, the MQTT payload).
     *
     * Prefer that field. Fall back to the raw one when it is absent so an older
     * API or an older gateway keeps behaving exactly as it does today - the
     * clients and the server do not deploy at the same instant, and the mobile
     * apps ship through the stores.
     *
     * fallbackRaw is used ONLY when speed_mph is absent. Note speed_mph covers
     * gps_speed alone, so a caller that also needs obd_speed must fold it in
     * itself rather than passing a combined value here.
     */
    function speedToMph(source, fallbackRaw = null) {
        const mph = source?.speed_mph;
        if (mph !== undefined && mph !== null) return parseFloat(mph);

        const raw = fallbackRaw !== null ? fallbackRaw : source?.gps_speed;
        const parsed = parseFloat(raw);
        return isNaN(parsed) ? 0 : parsed;
    }

    /**
     * As [speedToMph], but formatted for display in the user's preferred unit.
     *
     * A reading that is genuinely absent stays absent. The server distinguishes
     * "no fix" (null) from "stopped" (0) on purpose, and rendering both as 0
     * would tell the user a stationary vehicle when we simply have no speed.
     */
    function unitifySpeed(source, fallbackRaw = null) {
        const hasReading =
            (source?.speed_mph !== undefined && source?.speed_mph !== null) ||
            (fallbackRaw !== null && fallbackRaw !== undefined && fallbackRaw !== '') ||
            (source?.gps_speed !== undefined && source?.gps_speed !== null);
        if (!hasReading) return null;

        return unitifyDistance(speedToMph(source, fallbackRaw));
    }


    return {
        capitalize, formatEnumString, formatVoltage, unitifyDistance, speedToMph, unitifySpeed,
        unitifyVolume, unitifyTemperature, unitifyPressure, unitLabels
    }
}

export const isCompanyAdminRole = (user) => {
    if (!user || user.type !== 'company') return false;
    const role = (user.role || '').toLowerCase();
    return role.includes('admin');
};

// Clean Truck Check capabilities. Enroll and request-scan are open to
// company admins and managers; cancelling an enrollment is admin only.
// Platform admins can act for support.
export const canActOnCompliance = (user) => {
    if (!user) return false;
    if (user.type === 'admin') return true;
    if (user.type !== 'company') return false;
    const role = (user.role || '').toLowerCase();
    return role.includes('admin') || role.includes('manager');
};

export const canManageComplianceEnrollment = (user) => {
    if (!user) return false;
    if (user.type === 'admin') return true;
    return isCompanyAdminRole(user);
};

// VIN check-digit validation (SAE/NHTSA). Position 9 is a check digit derived
// from the other 16 characters. Returns true/false for 17-char VINs, null if
// it cannot be evaluated.
const VIN_TRANSLIT = {
    A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, J: 1, K: 2, L: 3, M: 4, N: 5,
    P: 7, R: 9, S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
    0: 0, 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 7: 7, 8: 8, 9: 9,
};
const VIN_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

export const vinCheckDigitValid = (vin) => {
    if (!vin || vin.length !== 17) return null;
    const v = vin.toUpperCase();
    let sum = 0;
    for (let i = 0; i < 17; i++) {
        if (!(v[i] in VIN_TRANSLIT)) return false;
        sum += VIN_TRANSLIT[v[i]] * VIN_WEIGHTS[i];
    }
    const rem = sum % 11;
    const check = rem === 10 ? 'X' : String(rem);
    return v[8] === check;
};

export const vinWarning = (vin) => {
    if (!vin) return null;
    const v = vin.toUpperCase();
    if (/[IOQ]/.test(v)) return 'VIN cannot contain the letters I, O or Q.';
    if (v.length !== 17) return 'VIN is not the standard 17 characters.';
    if (vinCheckDigitValid(v) === false) return 'VIN check digit does not validate.';
    return null;
};
